import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Transition, Popover } from "@headlessui/react";
import clsx from "clsx";
import axios from "axios";
import { captchaUrl, apiBaseUrl } from "../../services/http";
import Layout from "../../layouts";
import Breadcrumb from "../../components/Breadcrumb";
import Dropzone from "../../components/Dropzone";
import { AutoTabProvider } from "react-auto-tab";

export default function SuppInfo() {
  const navigate = useNavigate();
  const { token } = useParams();
  const [response, setResponse] = useState({});
  const [suppInfo, setSuppInfo] = useState({
    status: "idle",
    data: null,
    error: null,
  });
  const [submit, setSubmit] = useState({
    status: "idle",
    data: null,
    error: null,
  });

  useEffect(() => {
    const getSuppInfo = async () => {
      setSuppInfo({
        status: "pending",
        data: null,
        error: null,
      });
      try {
        const response = await axios.get(`${apiBaseUrl}/supp-info/${token}`, {
          headers: {
            "Content-Type": "application/json",
          },
        });
        setSuppInfo({
          status: "succeeded",
          data: response.data,
          error: null,
        });
      } catch (err) {
        setSuppInfo({
          status: "failed",
          data: null,
          error: err.response.data,
        });
      }
    };

    getSuppInfo();
  }, [token]);

  const onSubmit = async () => {
    const values = suppInfo.data.fields.filter(
      (field) => response[field.name] && response[field.name].trim() !== ''
    );
    if (values.length !== suppInfo.data.fields.length) {
      alert('All fields are required.');
      return;
    }
    setSubmit({
      status: "pending",
      data: null,
      error: null,
    });
    try {
      await axios.post(`${apiBaseUrl}/supp-info/${token}`, response, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      setSubmit({
        status: "succeeded",
        data: null,
        error: null,
      });
    } catch (err) {
      setSubmit({
        status: "failed",
        data: null,
        error: err.response.data.message,
      });
    }
  };

  const readFileASync = (file) => {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = reject;

      reader.readAsDataURL(file);
    });
  };

  const onPhotoRemoved = () => {
    setResponse(state => ({
      ...state,
      photo: null,
    }));
  };

  const onPhotoDropAccepted = async (file) => {
    const contentBuffer = await readFileASync(file);
    setResponse((state) => ({
      ...state,
      photo: contentBuffer,
    }));
  };

  const onPhotoDropRejected = (file) => {
    if (file.errors) {
      file.errors.forEach((error) => {
        alert(error.message);
        return;
      });
    }
  };

  const onAfcardRemoved = () => {
    setResponse((state) => ({
      ...state,
      afcard_copy: null,
    }));
  };

  const onAfcardDropAccepted = async (file) => {
    const contentBuffer = await readFileASync(file);
    setResponse((state) => ({
      ...state,
      afcard_copy: contentBuffer,
    }));
  };

  const onAfcardDropRejected = (file) => {
    if (file.errors) {
      file.errors.forEach((error) => {
        alert(error.message);
        return;
      });
    }
  };

  const onSchoolProofSelected = async (file) => {
    const contentBuffer = await readFileASync(file);
    setResponse((state) => ({
      ...state,
      school_proof: contentBuffer,
    }));
  };

  const onSchoolProofRemoved = () => {
    setResponse((state) => ({
      ...state,
      school_proof: null,
    }));
  };

  const renderField = (field) => {
    if (field.name === 'photo') {
      return (
        <div key={field.name} className="lg:flex">
          <div className="lg:w-72">
            <label className="inline-block">* {field.desc}:</label>
          </div>
          <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
            {!response.photo ? (
              <Dropzone
                width={"lg:w-40 w-full"}
                height={"lg:h-48 h-48"}
                format={"image"}
                minSize={50000}
                maxSize={3000000}
                isPassportPhoto={true}
                maxFiles={1}
                onAccepted={onPhotoDropAccepted}
                onRejected={onPhotoDropRejected}
              />
            ) : (
              <div>
                <div className="w-40 h-48">
                  <img
                    src={response.photo}
                    alt=""
                    className="h-full w-auto object-contain"
                  />
                </div>
                <button
                  className="text-red-600 text-sm mt-2 focus:outline-none hover:text-red-700"
                  onClick={onPhotoRemoved}
                >
                  <svg
                    class="w-4 h-4 inline-block mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>
      );
    } else if (field.name === "afcard_copy") {
      return (
        <div key={field.name} className="lg:flex">
          <div className="lg:w-72">
            <label className="inline-block">* {field.desc}:</label>
          </div>
          <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
            {!response.afcard_copy ? (
              <Dropzone
                width={"lg:w-96 w-full"}
                height={"lg:h-48 h-48"}
                format={"image"}
                maxSize={3000000}
                maxFiles={1}
                onAccepted={onAfcardDropAccepted}
                onRejected={onAfcardDropRejected}
              />
            ) : (
              <div>
                <div className="w-96 h-48">
                  <img
                    src={response.afcard_copy}
                    alt=""
                    className="h-full w-auto object-contain"
                  />
                </div>
                <button
                  className="text-red-600 text-sm mt-2 focus:outline-none hover:text-red-700"
                  onClick={onAfcardRemoved}
                >
                  <svg
                    class="w-4 h-4 inline-block mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>
      );
    } else if (field.name === "school_proof") {
      return (
        <div key={field.name} className="lg:flex">
          <div className="lg:w-72">
            <label className="inline-block">* {field.desc}:</label>
          </div>
          <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
            {!response.school_proof ? (
              <div>
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/gif, application/pdf"
                  maxFiles="1"
                  className="block p-3"
                  name="school_proof"
                  onChange={(e) => onSchoolProofSelected(e.target.files[0])}
                />
                <div>
                  <p className="text-gray-500">
                    File format: jpg / gif / png / pdf
                  </p>
                  <p className="text-gray-500">File size: 2MB</p>
                </div>
              </div>
            ) : (
              <div>
                <p>
                  <span className="rounded-lg border text-sm px-2 py-1 bg-gray-50">
                    1 file selected
                  </span>
                </p>
                <button
                  className="text-red-600 text-sm mt-2 focus:outline-none hover:text-red-700"
                  onClick={onSchoolProofRemoved}
                >
                  <svg
                    class="w-4 h-4 inline-block mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>
      );
    } else if (field.name === "id_number") {
      return (
        <div key={field.name} className="lg:flex">
          <div className="lg:w-72 lg:pt-3">
            <label className="inline-block">* {field.desc}:</label>
          </div>
          <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
            <>
              <AutoTabProvider settings={{pasteToFit:false}} className="flex flex-wrap space-x-2">
                {[...Array(8)].map((x, i) => {
                  if (i === 3 || i === 5 || i === 6) {
                    return (
                      <input
                        key={i}
                        type="text"
                        className="text-center bg-gray-50 border border-gray-300 rounded-lg w-14 p-3 mb-1 focus:border-orange-600 focus:ring-orange-600 disabled:bg-gray-200"
                        onPaste={e => e.preventDefault()}
                        value={response.id_number?.substring(i, i + 1).trim()}
                        onChange={(e) => {
                          const origNumber =
                            "***" +
                            (response.id_number?.[3] || " ") +
                            "*" +
                            (response.id_number?.[5] || " ") +
                            (response.id_number?.[6] || " ") +
                            "*";
                          const newNumber =
                            origNumber.substring(0, i) +
                            (e.target.value || " ") +
                            origNumber.substring(i + 1);

                          setResponse((state) => ({
                            ...state,
                            id_number: newNumber,
                          }));
                        }}
                        maxLength={1}
                        tabbable
                      />
                    );
                  }

                  return (
                    <div
                      key={i}
                      className="text-center bg-gray-200 border border-gray-300 rounded-lg w-14 p-3 mb-1"
                    >
                      *
                    </div>
                  );
                })}
              </AutoTabProvider>
              <div className="flex flex-wrap space-x-2 mb-2">
                {[...Array(8)].map((x, i) => (
                  <div
                    key={i}
                    className="w-14 text-center text-gray-500 text-xs"
                  >
                    {i + 1}
                    <sup>
                      {i === 0 ? "st" : i === 1 ? "nd" : i === 2 ? "rd" : "th"}
                    </sup>
                  </div>
                ))}
              </div>
              <p className="text-sm">
                Enter the 4th, 6th and 7th character of your HKID/passport
                number.
              </p>
              <p className="text-sm">
                For example, enter{" "}
                <b>
                  <u>3</u>
                </b>
                ,{" "}
                <b>
                  <u>5</u>
                </b>
                ,{" "}
                <b>
                  <u>6</u>
                </b>{" "}
                in 4th, 6th, 7th if your HKID is A12
                <b>
                  <u>3</u>
                </b>
                4
                <b>
                  <u>5</u>
                </b>
                <b>
                  <u>6</u>
                </b>
                (7).
              </p>
            </>
          </div>
        </div>
      );
    } else if (field.name === "id_type") {
      return (
        <div key={field.name} className="lg:flex">
          <div className="lg:w-72">
            <label className="inline-block">* {field.desc}:</label>
          </div>
          <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
            <div className="space-x-4">
              <label className="text-gray-900 inline-flex items-center">
                <input
                  type="radio"
                  name="id_type"
                  value="hkid"
                  className="w-4 h-4 border-gray-300 focus:ring-2 focus:ring-blue-300 mr-1"
                  checked={response.id_type === "hkid"}
                  onChange={() =>
                    setResponse((state) => ({
                      ...state,
                      id_type: "hkid",
                    }))
                  }
                />
                HKID
              </label>
              <label className="text-gray-900 inline-flex items-center">
                <input
                  type="radio"
                  name="id_type"
                  value="passport"
                  className="w-4 h-4 border-gray-300 focus:ring-2 focus:ring-blue-300 mr-1"
                  checked={response.id_type === "passport"}
                  onChange={() =>
                    setResponse((state) => ({
                      ...state,
                      id_type: "passport",
                    }))
                  }
                />
                Passport
              </label>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div key={field.name} className="lg:flex">
          <div className="lg:w-72 lg:pt-3">
            <label className="inline-block">* {field.desc}:</label>
          </div>
          <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
            <input
              type="text"
              className="bg-gray-50 border border-gray-300 rounded-lg block w-full p-3 focus:border-orange-600 focus:ring-orange-600"
              name={field.name}
              value={response[field.name]}
              onChange={(e) =>
                setResponse((state) => ({
                  ...state,
                  [field.name]: e.target.value,
                }))
              }
            />
          </div>
        </div>
      );
    }
  };

  return (
    <Layout>
      <div className="container mx-auto">
        <div className="lg:py-5 lg:px-0 px-4 py-4">
          <Breadcrumb currentPage="Submit Supplementary Information" />

          <div>
            <h3 className="font-bold text-lg mb-6">
              Submit Supplementary Information
            </h3>

            {submit.status === "succeeded" && (
              <div>
                <div className="flex flex-col items-center justify-center">
                  <svg
                    class="w-24 h-24 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  <h3 className="text-lg font-bold">Information received</h3>
                  <p>
                    Your application reference:{" "}
                    <span className="text-blue-600 font-bold">
                      {suppInfo.data.app}
                    </span>
                  </p>
                </div>
                <div className="max-w-xl mx-auto mt-5 space-y-5">
                  <div>
                    <h3 className="font-bold mb-3">What happens next</h3>
                    <ul className="list-disc space-y-2 ml-5">
                      <li>
                        You could be asked for providing more documents and
                        information if the information you provided is not
                        sufficient.
                      </li>
                      <li>
                        A payment link shall be sent to you if there is any
                        amount due in your application.
                      </li>
                      <li>
                        Library reserves the right of final decision on the
                        eligibility of your application.
                      </li>
                      <li>
                        Use the above application reference to keep track of
                        your application status. For assistance, please contact
                        us by e-mail{" "}
                        <a
                          href="mailto:libmember@eduhk.hk"
                          className="text-orange-600 hover:text-orange-700 underline"
                        >
                          libmember@eduhk.hk
                        </a>
                        .
                      </li>
                    </ul>
                  </div>
                  <div className="flex items-center justify-center">
                    <a
                      className="border border-orange-600 rounded-full px-5 py-3 text-center bg-orange-600 text-white hover:bg-orange-700 transition duration-200 focus:outline-none inline-flex items-center"
                      href="https://www.lib.eduhk.hk"
                    >
                      <svg
                        className="w-5 h-5 mr-1"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                        ></path>
                      </svg>
                      Library Home Page
                    </a>
                  </div>
                </div>
              </div>
            )}

            {submit.status !== "succeeded" &&
              suppInfo.status === "succeeded" && (
                <div>
                  {suppInfo.data.remarks && (
                    <div className="mb-2 border-l-4 pl-2 py-2">
                      <span className="font-bold">Remarks:</span>{" "}
                      {suppInfo.data.remarks}
                    </div>
                  )}
                  <div className="border large-shadow mb-8">
                    <div className="lg:px-8 lg:py-8 px-8 py-4 space-y-4">
                      <div className="lg:flex">
                        <div className="lg:w-72">
                          <label className="inline-block">
                            Application Reference:
                          </label>
                        </div>
                        <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                          {suppInfo.data.app}
                        </div>
                      </div>
                      <div className="lg:flex">
                        <div className="lg:w-72">
                          <label className="inline-block">E-mail:</label>
                        </div>
                        <div className="lg:flex-1 lg:ml-4 lg:mt-0 mt-2">
                          {suppInfo.data.email}
                        </div>
                      </div>
                      {suppInfo.data.fields.map((field, i) =>
                        renderField(field)
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-center my-8">
                    <button
                      className="border border-orange-600 rounded-full px-4 py-2 w-48 text-center inline-block bg-orange-600 text-white ml-8 hover:bg-orange-700 transition duration-200 focus:outline-none disabled:opacity-50 disabled:pointer-events-none"
                      onClick={onSubmit}
                      disabled={submit.status === "pending"}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              )}
            {suppInfo.status === "failed" && (
              <div
                class="flex p-4 mb-8 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
                role="alert"
              >
                <svg
                  aria-hidden="true"
                  class="flex-shrink-0 inline w-5 h-5 mr-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                <div>
                  <span class="font-medium">Error:</span>{" "}
                  {suppInfo.error.message}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
